import { useMutation, useQueryClient, useQuery, UseQueryResult } from "@tanstack/react-query";
import axios from "axios";

export interface TcDataRequestDto {
  marksheetId: number;
  classId: number;
  sessionId: number;
  resultClass: string;
  result: string;
  nextClass: string;
  yearOfResult: string;
  dateOfLeaving: string; // yyyy-mm-dd
  totalDays: number;
  presentDays: number;
  behaviour: string;
  nextSchoolDetails: string;
  nextSchoolStatus: string;
}

const API_URL = `${process.env.REACT_APP_API_URL}/tc-data`;

// Optional response DTO (shape may vary by backend). Adjust if needed.
export interface TcDataResponseDto {
  id: number;
  marksheet: {
    id: number;
    [key: string]: any; // Allow other marksheet fields
  };
  resultClass: string;
  result: string;
  nextClass: string;
  yearOfResult: string;
  dateOfLeaving: string | null; // dd/MM/yyyy or null
  totalDays: number;
  presentDays: number;
  behaviour: string;
  nextSchoolDetails: string;
  nextSchoolStatus: string;
  [key: string]: any; // Allow other fields like session, schoolClass, etc.
}

const toDdMmYyyy = (value?: string): string => {
  if (!value) return "";
  if (value.includes("/")) return value; // already dd/MM/yyyy
  const parts = value.split("-");
  if (parts.length === 3) {
    const [y, m, d] = parts;
    return `${d}/${m}/${y}`;
  }
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  }
  return value;
};

const addTcData = async (data: TcDataRequestDto): Promise<void> => {
  const payload = {
    ...data,
    dateOfLeaving: toDdMmYyyy(data.dateOfLeaving),
  };
  await axios.post(`${API_URL}/add`, payload);
};

// GET: /tc-data/get/{sessionId}/{classId}
const getTcData = async (sessionId: number, classId: number): Promise<TcDataResponseDto[]> => {
  const res = await axios.get(`${API_URL}/get/${sessionId}/${classId}`);
  return res.data;
};

// GET: /tc-data/{sessionId}/{classId}?studentCode={studentCode}
const getTcByStudentCode = async (
  sessionId: number,
  classId: number,
  studentCode: string
): Promise<TcDataResponseDto> => {
  const res = await axios.get(`${API_URL}/${sessionId}/${classId}`, {
    params: { studentCode },
  });
  return res.data;
};

export function useTdData() {
  const queryClient = useQueryClient();

  const addTcDataMutation = useMutation({
    mutationFn: addTcData,
    onSuccess: () => {
      // Invalidate all TC-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["tclist"] });
      queryClient.invalidateQueries({ queryKey: ["tcdata"] });
      // Also clear the cache completely for TC data to prevent cross-school data leakage
      queryClient.removeQueries({ queryKey: ["tcdata"] });
    },
  });

  // Query hook for fetching TC data
  const useTcData = (
    sessionId: number | undefined,
    classId: number | undefined,
    schoolId: number | undefined,
    enabled: boolean = true
  ): UseQueryResult<TcDataResponseDto[], Error> =>
    useQuery<TcDataResponseDto[], Error>({
      queryKey: ["tcdata", sessionId, classId, schoolId],
      queryFn: () => {
        if (!sessionId || !classId || Number(sessionId) <= 0 || Number(classId) <= 0) return Promise.resolve([]);
        return getTcData(sessionId, classId);
      },
      enabled: enabled && Number(sessionId) > 0 && Number(classId) > 0,
      staleTime: 0, // Always fetch fresh data
      cacheTime: 0, // Don't cache old data
    });

  return {
    addTcDataMutation,
    getTcData,
    getTcByStudentCode,
    useTcData,
  };
}


